import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Table, Badge, Spinner, Button, Form, InputGroup, Row, Col, Card, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import SignalDetails from './SignalDetails';
import { signalsApi } from '../services/api';

// Weights for the 4 chain timeframes (must match backend)
const CHAIN_WEIGHTS = { '1h': 0.25, '4h': 0.5, 'D': 1, 'W': 2 };

const SignalTable = ({ 
  signals, 
  loading, 
  isActiveTrades = false,
  onFilterChange = null,
  initialFilters = {},
  onSignalsDeleted = null,
}) => {
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // State for filtering and sorting
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm || '');
  const [filterSignalType, setFilterSignalType] = useState(initialFilters.signalType || 'all');
  const [filterTimeframe, setFilterTimeframe] = useState(initialFilters.timeframe || 'all');
  const [sortField, setSortField] = useState(initialFilters.sortField || 'timestamp');
  const [sortDirection, setSortDirection] = useState(initialFilters.sortDirection || 'desc');
  
  // State for signal selection and deletion
  const [selectedSignals, setSelectedSignals] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteMode, setDeleteMode] = useState('selected'); // 'selected', 'all', 'single'
  const [signalToDelete, setSignalToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Track initial mount
  const isInitialMount = useRef(true);
  const lastFilters = useRef('');
  
  // Handle filter changes with debounce for search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  
  // Predefined lists for filters
  const uniqueSignalTypes = [
    'all', 
    'long', 
    'short', 
    'mean reversal long', 
    'mean reversal short', 
    'strong mean reversal long', 
    'strong mean reversal short',
    'kernel change bull',
    'kernel change bear',
    'long pullback',
    'short pullback'
  ];
  
  const uniqueTimeframes = [
    'all',
    '1m',
    '3m',
    '5m',
    '15m', 
    '30m',
    '1h',
    '2h',
    '4h',
    'D',
    'W',
    'M'
  ]; 
  
  // Add any additional timeframes from signals
  if (signals && signals.length > 0) {
    const additionalTimeframes = [...new Set(signals
      .filter(s => s.timeframe && !uniqueTimeframes.includes(s.timeframe))
      .map(s => s.timeframe))];
    
    if (additionalTimeframes.length > 0) {
      uniqueTimeframes.push(...additionalTimeframes);
    }
  }
  
  // Clear selected signals when signals change
  useEffect(() => {
    setSelectedSignals([]);
    setSelectAll(false);
  }, [signals]);
  
  const handleSearchSubmit = () => {
    setDebouncedSearchTerm(searchTerm);
  };
  
  // Update parent component when filters change
  useEffect(() => {
    // Skip the first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Add a simple check to avoid redundant calls
    if (onFilterChange && lastFilters.current !== JSON.stringify({
      searchTerm: debouncedSearchTerm,
      signalType: filterSignalType,
      timeframe: filterTimeframe,
      sortField,
      sortDirection
    })) {
      console.log('Filters changed, updating...');
      
      // Store current filters to compare next time
      lastFilters.current = JSON.stringify({
        searchTerm: debouncedSearchTerm,
        signalType: filterSignalType,
        timeframe: filterTimeframe,
        sortField,
        sortDirection
      });
      
      // Now it's safe to call onFilterChange
      onFilterChange({
        searchTerm: debouncedSearchTerm,
        signalType: filterSignalType,
        timeframe: filterTimeframe,
        sortField,
        sortDirection
      });
    }
  }, [debouncedSearchTerm, filterSignalType, filterTimeframe, sortField, sortDirection, onFilterChange]);
  
  // Open signal details modal
  const handleShowDetails = (signal) => {
    setSelectedSignal(signal);
    setShowDetails(true);
  };
  
  // Close signal details modal
  const handleCloseDetails = () => {
    setShowDetails(false);
  };
  
  // Handle selection of a signal
  const handleSignalSelect = useCallback((signalId) => {
    setSelectedSignals(prev => {
      if (prev.includes(signalId)) {
        return prev.filter(id => id !== signalId);
      } else {
        return [...prev, signalId];
      }
    });
  }, []);
  
  // Handle select all signals
  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedSignals([]);
    } else {
      const allIds = signals.map(signal => signal.id);
      setSelectedSignals(allIds);
    }
    setSelectAll(!selectAll);
  }, [signals, selectAll]);
  
  // Request to delete a single signal
  const handleDeleteSignal = (signal) => {
    setSignalToDelete(signal);
    setDeleteMode('single');
    setShowDeleteConfirm(true);
  };
  
  // Request to delete selected signals
  const handleDeleteSelected = () => {
    if (selectedSignals.length === 0) return;
    
    setDeleteMode('selected');
    setShowDeleteConfirm(true);
  };
  
  // Request to delete all signals
  const handleDeleteAll = () => {
    if (signals.length === 0) return;
    
    setDeleteMode('all');
    setShowDeleteConfirm(true);
  };
  
  // Execute signal deletion
  const executeDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      let result;
      
      if (deleteMode === 'single' && signalToDelete) {
        // Delete a single signal
        result = await signalsApi.deleteSignal(signalToDelete.id);
      } else if (deleteMode === 'selected' && selectedSignals.length > 0) {
        // Delete selected signals
        result = await signalsApi.deleteMultipleSignals(selectedSignals);
      } else if (deleteMode === 'all') {
        // Delete all signals (with optional filters)
        const filters = {
          searchTerm: debouncedSearchTerm,
          signalType: filterSignalType,
          timeframe: filterTimeframe
        };
        result = await signalsApi.deleteAllSignals(filters, true);
      }
      
      // Clear selections
      setSelectedSignals([]);
      setSelectAll(false);
      
      // Notify parent that signals were deleted
      if (onSignalsDeleted) {
        onSignalsDeleted(result);
      }
      
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting signals:', error);
      setDeleteError(error.response?.data?.error || 'Failed to delete signals');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Handle sort click on table headers
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle direction if already sorting by this field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setFilterSignalType('all');
    setFilterTimeframe('all');
    setSortField('timestamp');
    setSortDirection('desc');
  };
  
  // Function to format date
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit' 
    };
    return new Date(dateString).toLocaleString(undefined, options);
  };
  
  // Function to convert symbol to uppercase
  const formatSymbol = (symbol) => {
    return symbol ? symbol.toUpperCase() : '';
  };
  
  // Function to get badge variant based on signal type
  const getSignalBadgeClass = (signalType) => {
    const baseClass = 'signal-badge ';
    
    if (signalType === 'long') {
      return baseClass + 'signal-badge-long';
    } else if (signalType === 'short') {
      return baseClass + 'signal-badge-short';
    } else if (signalType === 'mean reversal long') {
      return baseClass + 'signal-badge-mean-reversal-long';
    } else if (signalType === 'mean reversal short') {
      return baseClass + 'signal-badge-mean-reversal-short';
    } else if (signalType === 'strong mean reversal long') {
      return baseClass + 'signal-badge-strong-mean-reversal-long';
    } else if (signalType === 'strong mean reversal short') {
      return baseClass + 'signal-badge-strong-mean-reversal-short';
    } else if (signalType === 'kernel change bull') {
      return baseClass + 'signal-badge-kernel-change-bull';
    } else if (signalType === 'kernel change bear') {
      return baseClass + 'signal-badge-kernel-change-bear';
    } else if (signalType === 'long pullback') {
      return baseClass + 'signal-badge-long-pullback';
    } else if (signalType === 'short pullback') {
      return baseClass + 'signal-badge-short-pullback';
    }

    return baseClass;
  };
  
  // Render chain confluence badges from stored signal metadata
  const renderTrendAlignmentIcons = (signal) => {
    const chainWeightUp = signal.chain_weight_up || 0;
    const chainWeightDown = signal.chain_weight_down || 0;
    const chainTfsUp = signal.chain_timeframes_up || [];
    const chainTfsDown = signal.chain_timeframes_down || [];

    if (chainWeightUp === 0 && chainWeightDown === 0) return null;

    const hasBoth = chainWeightUp > 0 && chainWeightDown > 0;

    const buildTooltip = (weight, timeframes, isUp) => {
      const tfDetail = timeframes
        .map(tf => `${tf} (${CHAIN_WEIGHTS[tf]}x)`)
        .join(' + ');
      return (
        <div className="trend-tooltip">
          <div className="trend-tooltip-title">
            {weight}x {isUp ? 'Bullish' : 'Bearish'} Alignment
          </div>
          {tfDetail && (
            <>
              <hr className="my-1" />
              <div className="trend-tooltip-content">{tfDetail}</div>
            </>
          )}
          {hasBoth && (
            <div className="mt-1" style={{ color: '#ffc107', fontSize: '0.8rem' }}>
              ⚠️ Mixed timeframe signals
            </div>
          )}
        </div>
      );
    };

    return (
      <>
        {chainWeightUp > 0 && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`chain-up-${signal.id}`} className="trend-tooltip-wrapper">
                {buildTooltip(chainWeightUp, chainTfsUp, true)}
              </Tooltip>
            }
          >
            <span className="ms-2 chain-badge chain-badge-up">
              {chainWeightUp}x↑
            </span>
          </OverlayTrigger>
        )}
        {chainWeightDown > 0 && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`chain-down-${signal.id}`} className="trend-tooltip-wrapper">
                {buildTooltip(chainWeightDown, chainTfsDown, false)}
              </Tooltip>
            }
          >
            <span className="ms-2 chain-badge chain-badge-down">
              {chainWeightDown}x↓
            </span>
          </OverlayTrigger>
        )}
      </>
    );
  };
  
  // Render sort indicator
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    
    return (
      <span className="ms-1">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Function to get the confirmation message based on delete mode
  const getDeleteConfirmMessage = () => {
    if (deleteMode === 'single' && signalToDelete) {
      return `Are you sure you want to delete the signal for ${formatSymbol(signalToDelete.symbol)} (${signalToDelete.signal})?`;
    } else if (deleteMode === 'selected') {
      return `Are you sure you want to delete ${selectedSignals.length} selected signals?`;
    } else if (deleteMode === 'all') {
      let message = `Are you sure you want to delete ALL signals`;
      const filterCount = [
        debouncedSearchTerm ? 1 : 0,
        filterSignalType !== 'all' ? 1 : 0,
        filterTimeframe !== 'all' ? 1 : 0
      ].reduce((a, b) => a + b, 0);
      
      if (filterCount > 0) {
        message += ` matching your current filters (${signals.length} signals)`;
      } else {
        message += ` (${signals.length} signals)`;
      }
      
      message += `? This action cannot be undone.`;
      return message;
    }
    
    return 'Are you sure you want to delete these signals?';
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  
  return (
    <>
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row>
            <Col>
              <h5 className="mb-3">Filter Signals</h5>
            </Col>
            <Col xs="auto">
              <div className="d-flex gap-2">
                {selectedSignals.length > 0 && (
                  <Button 
                    variant="danger" 
                    size="sm"
                    onClick={handleDeleteSelected}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Delete Selected ({selectedSignals.length})
                  </Button>
                )}
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={handleDeleteAll}
                  disabled={signals.length === 0}
                >
                  <i className="bi bi-trash-fill me-1"></i>
                  Delete All
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <InputGroup>
                <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                <Form.Control
                  placeholder="Search by symbol or signal type"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => { setSearchTerm(''); setDebouncedSearchTerm(''); }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </Button>
                )}
                <Button variant="outline-secondary" onClick={handleSearchSubmit}>
                  Search
                </Button>
              </InputGroup>
            </Col>
            
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Signal Type</Form.Label>
                <Form.Select
                  value={filterSignalType}
                  onChange={(e) => setFilterSignalType(e.target.value)}
                >
              {uniqueSignalTypes.map(type => (
                    <option key={type} value={type}>
                      {type === 'all' ? 'All Signal Types' : type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Timeframe</Form.Label>
                <Form.Select
                  value={filterTimeframe}
                  onChange={(e) => setFilterTimeframe(e.target.value)}
                >
                  {uniqueTimeframes.map(timeframe => (
                    <option key={timeframe} value={timeframe}>
                      {timeframe === 'all' ? 'All Timeframes' : timeframe}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Badge bg="secondary">{signals.length} signal{signals.length !== 1 ? 's' : ''} found</Badge>
            </div>
            <div>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={handleResetFilters}
              >
                <i className="bi bi-arrow-repeat me-1"></i>
                Reset Filters
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
      
      {!signals || signals.length === 0 ? (
        <div className="text-center my-4 py-5 bg-light rounded">
          <i className="bi bi-filter-circle text-muted" style={{ fontSize: '2rem' }}></i>
          <p className="mt-3 mb-0 text-muted">
            {isActiveTrades 
              ? 'No signals found for your active trades. Add symbols to your watchlist or adjust your filters.'
              : 'No signals match your filters.'}
          </p>
          <Button 
            variant="link" 
            onClick={handleResetFilters}
          >
            Clear all filters
          </Button>
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover className="signal-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Form.Check
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    title="Select all signals"
                  />
                </th>
                <th onClick={() => handleSort('symbol')} style={{ cursor: 'pointer' }}>
                  Symbol {renderSortIcon('symbol')}
                </th>
                <th onClick={() => handleSort('signal')} style={{ cursor: 'pointer' }}>
                  Signal {renderSortIcon('signal')}
                </th>
                <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>
                  Price {renderSortIcon('price')}
                </th>
                <th onClick={() => handleSort('timeframe')} style={{ cursor: 'pointer' }}>
                  Timeframe {renderSortIcon('timeframe')}
                </th>
                <th onClick={() => handleSort('timestamp')} style={{ cursor: 'pointer' }}>
                  Time {renderSortIcon('timestamp')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {signals.map((signal) => (
                <tr key={signal.id} className={`signal-row-${signal.signal.replace(/\s+/g, '-')}`}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedSignals.includes(signal.id)}
                      onChange={() => handleSignalSelect(signal.id)}
                    />
                  </td>
                  <td className="fw-bold">{formatSymbol(signal.symbol)}</td>
                  <td>
                    <span className={getSignalBadgeClass(signal.signal)}>
                      {signal.signal}
                    </span>
                    {renderTrendAlignmentIcons(signal)}
                  </td>
                  <td>{parseFloat(signal.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>{signal.timeframe || '-'}</td>
                  <td>{formatDate(signal.timestamp)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => handleShowDetails(signal)}
                      >
                        <i className="bi bi-info-circle me-1"></i>
                        Details
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteSignal(signal)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      
      {/* Signal Details Modal */}
      <SignalDetails 
        signal={selectedSignal}
        allSignals={signals}
        show={showDetails}
        onHide={handleCloseDetails}
      />
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError ? (
            <div className="alert alert-danger">
              {deleteError}
            </div>
          ) : (
            <p>{getDeleteConfirmMessage()}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={executeDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner as="span" size="sm" animation="border" role="status" className="me-2" />
                Deleting...
              </>
            ) : (
              <>Delete</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SignalTable;